import { useEffect, useState } from 'react';
import { useMediaQuery } from '../../lib/useMediaQuery';
import { useReducedMotion } from '../../lib/useReducedMotion';

const CHECKS = [
  ['Words to avoid', 'none found'],
  ['Character limit', '214 / 3,000'],
  ['Fact check', '2 of 2 supported'],
  ['Contrast on the slide', '13.3 : 1'],
] as const;

/** One tick per check, then a beat of "ready for you" before it starts over. */
const CYCLE = CHECKS.length + 4;
const TICK_MS = 850;

const TRUST = ['Written from your facts', 'Every claim checked', 'Nothing posts without you'];

/**
 * The dark panel beside the auth form from 980px: a sample post whose checks
 * tick through one by one. Not rendered (and not ticking) below that width;
 * reduced motion shows every check done and nothing moves.
 */
export function AuthAside() {
  const isWide = useMediaQuery('(min-width: 980px)');
  const isReduced = useReducedMotion();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!isWide || isReduced) return;
    const timer = window.setInterval(() => setTick((value) => (value + 1) % CYCLE), TICK_MS);
    return () => window.clearInterval(timer);
  }, [isWide, isReduced]);

  if (!isWide) return null;

  const step = isReduced ? CHECKS.length + 1 : tick;
  const isAllDone = step > CHECKS.length;

  return (
    <aside className="auth-aside" aria-label="What Autopostmate checks">
      <div className="auth-aside__grid" aria-hidden="true" />
      <div className="auth-aside__orb auth-aside__orb--indigo" aria-hidden="true" />
      <div className="auth-aside__orb auth-aside__orb--amber" aria-hidden="true" />

      <div className="auth-aside__intro">
        <p className="auth-aside__pill">
          <span className="auth-aside__pill-dot" aria-hidden="true" />
          Every post leaves checked
        </p>
        <h2 className="auth-aside__headline">
          Your facts in. <span>Checked posts out.</span>
        </h2>
      </div>

      <div className="auth-aside__card">
        <div className="auth-aside__byline">
          <span className="auth-aside__avatar" aria-hidden="true">
            SM
          </span>
          <span className="auth-aside__who">
            <strong>Sara Malik</strong>
            LinkedIn · Version A
          </span>
          <span
            className={isAllDone ? 'auth-aside__chip auth-aside__chip--ready' : 'auth-aside__chip'}
          >
            {isAllDone ? 'ready for you' : 'checking…'}
          </span>
        </div>
        <p className="auth-aside__post-title">What a 13-week cash forecast caught last quarter.</p>
        <p className="auth-aside__post-body">
          A payroll run landing three days before a customer paid. I’ve been CFO to 9 startups since
          2018; this is the forecast I build first.
        </p>
        <ul className="auth-aside__checks">
          {CHECKS.map(([label, value], index) => {
            const isDone = step > index;
            const isChecking = step === index;
            const state = isDone
              ? ' auth-aside__check--done'
              : isChecking
                ? ' auth-aside__check--checking'
                : '';
            return (
              <li key={label} className={`auth-aside__check${state}`}>
                <span className="auth-aside__check-mark" aria-hidden="true">
                  {isDone ? '✓' : ''}
                </span>
                <span className="auth-aside__check-label">{label}</span>
                <span className="auth-aside__check-value">
                  {isDone ? value : isChecking ? 'checking' : '—'}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      <ul className="auth-aside__trust">
        {TRUST.map((line) => (
          <li key={line}>
            <span className="auth-aside__tick" aria-hidden="true">
              ✓
            </span>
            {line}
          </li>
        ))}
      </ul>
    </aside>
  );
}
